import {
  IDateFilterParams,
  IRowNode,
  TextMatcherParams,
  ValueFormatterFunc,
} from '@ag-grid-community/core';
import { formatPhoneNumber } from '@utils/phoneNumberFormat';
import { DateTime } from 'luxon';

export {
  clearRelativeDates,
  relativeDatesForGrid,
  relativeDatesForSave,
} from './relativeDateFilters';

export const startOfLocalDay = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate());

export const getLocalToday = (): Date => startOfLocalDay(new Date());

export const dateComparator = (
  date1: Date | string,
  date2: Date | string,
): number => {
  const dateValue1 = date1 instanceof Date ? date1 : new Date(date1);
  const dateValue2 = date2 instanceof Date ? date2 : new Date(date2);

  if (isNaN(dateValue1.getTime()) || isNaN(dateValue2.getTime())) {
    return 0;
  }

  const normalizedDate1 = new Date(
    dateValue1.getFullYear(),
    dateValue1.getMonth(),
    dateValue1.getDate(),
  );

  const normalizedDate2 = new Date(
    dateValue2.getFullYear(),
    dateValue2.getMonth(),
    dateValue2.getDate(),
  );

  if (normalizedDate2 < normalizedDate1) {
    return -1;
  } else if (normalizedDate2 > normalizedDate1) {
    return 1;
  }

  return 0;
};

export const dateFilterParams: IDateFilterParams = {
  minValidYear: 1900,
  maxValidYear: new Date().getFullYear() + 1,
  defaultOption: 'equals',
  comparator: dateComparator,
  maxNumConditions: 100,
  numAlwaysVisibleConditions: 1,
  defaultJoinOperator: 'AND',
  buttons: ['reset'],
  filterOptions: ['equals', 'lessThan', 'greaterThan', 'inRange'],
};

/** Per-column date filter params so rolling Today/Tomorrow/Yesterday bind to one field. */
export type DateFilterParamsWithCol = IDateFilterParams & {
  relativeDateColId: string;
};

export const dateFilterParamsFor = (
  relativeDateColId: string,
): DateFilterParamsWithCol => ({
  ...dateFilterParams,
  relativeDateColId,
});

export const timeComparator = (
  date1: Date | string,
  date2: Date | string,
): number => {
  const dateValue1 = date1 instanceof Date ? date1 : new Date(date1);
  const dateValue2 = date2 instanceof Date ? date2 : new Date(date2);

  if (isNaN(dateValue1.getTime()) || isNaN(dateValue2.getTime())) {
    return 0;
  }

  const d1 = new Date();
  d1.setHours(
    dateValue1.getHours(),
    dateValue1.getMinutes(),
    dateValue1.getSeconds(),
  );
  const d2 = new Date();
  d2.setHours(
    dateValue2.getHours(),
    dateValue2.getMinutes(),
    dateValue2.getSeconds(),
  );

  if (d2 < d1) {
    return -1;
  } else if (d2 > d1) {
    return 1;
  }

  return 0;
};

export const sortKeyComparatorFactory = <
  T extends Record<string, unknown> | object,
>(
  key: keyof T,
  isStickyValue?: (v: T) => boolean,
) => {
  return (a: T, b: T, nodeA?: IRowNode<T>, nodeB?: IRowNode<T>) => {
    if (
      ((!!nodeA?.data && isStickyValue?.(nodeA.data)) ?? false) ||
      ((!!nodeB?.data && isStickyValue?.(nodeB.data)) ?? false)
    ) {
      return 0;
    }

    if (nodeA === undefined || nodeA.data === undefined) {
      return 1;
    } else if (nodeB === undefined || nodeB.data === undefined) {
      return -1;
    }

    if (nodeA.data[key] >= nodeB.data[key]) {
      return 1;
    }

    return -1;
  };
};

/**
 * This function converts the column value (which is a date) into a string of
 * `HH:mm` format, so that we can freely match against the time.
 * @param params This contains things like the filter text, value, & grid api.
 * @returns The matched value or `null`.
 */
export const hourAndMinuteMatcherFromText = (
  params: TextMatcherParams,
): string | null => {
  if (!Boolean(params.filterText)) {
    return params.value as string;
  }

  // The filter text could contain trailing whitespaces. So we remove them here.
  // If we don't someone could enter "  13:45" and it would not match.
  const filterText = (params.filterText as string).trim();

  // The date format could be anything, but it will always be supported by JS.
  // I didn't want to add any complex logic in here, so I parse as a JS date &
  // then I parse in Luxon.
  const date = new Date((params.value as string).toUpperCase());
  const lDate = DateTime.fromJSDate(date);
  const time = lDate.toFormat('HH:mm');

  if (time.includes(filterText)) {
    return params.value as string;
  }

  return null;
};

export const dateFormatter: ValueFormatterFunc<unknown, Date> = (param) => {
  let date = param.value;
  if (date === undefined || date === null) {
    return '';
  }

  date = new Date(date);

  if (date.toString() === 'Invalid Date') {
    return '';
  }

  return `${DateTime.fromJSDate(date).toFormat('MM/dd/yyyy')}`;
};

export const fullDateFormatter: ValueFormatterFunc<unknown, Date> = (param) => {
  let date = param.value;

  if (date === undefined || date === null) {
    return '';
  }

  date = new Date(date);

  if (date.toString() === 'Invalid Date') {
    return '';
  }

  return `${DateTime.fromJSDate(date).toFormat('MM/dd/yyyy HH:mm')}`;
};

export const fullDateFormatterLocal: ValueFormatterFunc<unknown, Date> = (
  param,
) => {
  let date = param.value as string | undefined;

  if (!date) {
    return '';
  }

  if (!date.includes('Z')) {
    date = `${date}Z`;
  }

  const jsDate = new Date(date);

  if (jsDate.toString() === 'Invalid Date') {
    return '';
  }

  return `${DateTime.fromJSDate(jsDate).toUTC().toLocal().toFormat('MM/dd/yyyy HH:mm')}`;
};

export const fullDateFormatterWithTime: ValueFormatterFunc<unknown, Date> = (
  param,
) => {
  let date = param.value;

  if (date === undefined || date === null) {
    return '';
  }

  date = new Date(date);

  if (date.toString() === 'Invalid Date') {
    return '';
  }

  return `${DateTime.fromJSDate(date).toFormat('MM/dd/yyyy hh:mm a')}`;
};

export const underlinedCellRenderer = (params: {
  value: string | Date | undefined;
}) => {
  const div = document.createElement('u');
  div.style.cursor = 'pointer';

  if (params.value instanceof Date) {
    // @ts-expect-error - The error here doesn't really matter.
    div.innerHTML = dateFormatter(params);
  } else {
    div.innerHTML = params.value ?? '';
  }

  return div;
};

// Last Check is already in UTC
export const lastCheckDateFormatter: ValueFormatterFunc<unknown, Date> = ({
  value,
}) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (date.toString() === 'Invalid Date') {
    return '';
  }

  return `${DateTime.fromJSDate(date).toFormat('MM/dd/yyyy HH:mm')}`;
};

export const fullDateFormatterYear: ValueFormatterFunc<unknown, Date> = (
  param,
) => {
  let date = param.value;

  if (date === undefined || date === null) {
    return '';
  }

  date = new Date(date);

  if (date.toString() === 'Invalid Date') {
    return '';
  }

  return `${DateTime.fromJSDate(date).toLocal().toFormat('MM/dd/yyyy - T')}`;
};

export const fullDateFormatterYearNoTime: ValueFormatterFunc<unknown, Date> = (
  param,
) => {
  let date = param.value;

  if (date === undefined || date === null) {
    return '';
  }

  date = new Date(date);

  if (date.toString() === 'Invalid Date') {
    return '';
  }

  return `${DateTime.fromJSDate(date).toLocal().toFormat('MM/dd/yyyy')}`;
};

export const timeFormatter: ValueFormatterFunc<unknown, Date> = (param) => {
  let date = param.value;

  if (date === undefined || date === null) {
    return '';
  }

  date = new Date(date);

  if (date.toString() === 'Invalid Date') {
    return '';
  }

  return DateTime.fromJSDate(date).toFormat('ttt');
};

export const currencyFormatter = (amount: number, currency = 'USD') => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  });

  return formatter.format(amount);
};

export const currencyCellFormatter: ValueFormatterFunc<unknown, number> = (
  param,
) => {
  const amount = param.value;

  if (amount === undefined || amount === null) {
    return '';
  }

  return currencyFormatter(amount);
};

export const weightValueCellFormatter: ValueFormatterFunc<unknown, number> = (
  param,
) => {
  if (param.value === undefined || param.value === null) {
    return '';
  }

  const weight = parseFloat(String(param.value));

  if (isNaN(weight)) {
    return '';
  }

  return weight.toFixed(3);
};

export const phoneNumberCellFormatter: ValueFormatterFunc<unknown, string> = (
  param,
) => {
  const number = param.value;

  if (number === undefined || number === null) {
    return '';
  }

  return formatPhoneNumber(number);
};

export const timeAgoFormatter = (params: {
  data: {
    lastActionDate: string | null;
  };
}) => {
  const parseDate = (input: string | null): Date | null => {
    if (Boolean(input)) {
      if (isNaN(Date.parse(input!))) {
        const inputStr = input!.toString().replace(/\D/g, '');
        const d = new Date(parseInt(inputStr, 10));
        return d;
      }
      return new Date(Date.parse(input!));
    }
    return null;
  };

  const jsDate = parseDate(params.data.lastActionDate);
  if (!jsDate) return '';

  const now = DateTime.local();
  const actionDate = DateTime.fromJSDate(jsDate);
  const diffInDays = now.diff(actionDate, 'days').days;

  // Show in appropriate units with rounding
  if (diffInDays < 30) {
    // Less than a month, show in days
    const days = Math.round(diffInDays);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  } else if (diffInDays < 365) {
    // Less than a year, show in months
    const months = Math.round(diffInDays / 30);
    return `${months} month${months === 1 ? '' : 's'} ago`;
  }
  // More than a year, show in years
  const years = Math.round(diffInDays / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
};

export const compareDates = (
  valueA: string | null,
  valueB: string | null,
): number => {
  if (!Boolean(valueA) && !Boolean(valueB)) return 0;
  if (!Boolean(valueA)) return -1;
  if (!Boolean(valueB)) return 1;

  const dateA = DateTime.fromFormat(valueA!, 'MM/dd/yyyy');
  const dateB = DateTime.fromFormat(valueB!, 'MM/dd/yyyy');

  if (!dateA.isValid || !dateB.isValid) return 0;

  if (dateA < dateB) return -1;
  if (dateA > dateB) return 1;
  return 0;
};
